'use client'

/**
 * The face of an AI coworker (plan 03 CHG-12, CHG-14, D7), ported from the PA
 * build (h2r components/h2r/CoworkerAvatar.tsx).
 *
 * Changed on the way over: the PA build resolved a face from a hard-coded UK
 * roster and a table of renamed functions. Here identity comes from the API
 * (the `coworker_roster` Setting, or the persona's `photo`), so the component
 * holds no names and can move into the template (CHG-19). Photos live in
 * `public/coworkers/<photo>.jpg`; Ava's is a female photo (D7).
 *
 * A missing or broken photo falls back to an initial on a brand colour, never a
 * broken image in front of a room. Colours are brand tokens, not raw hex.
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'

const TONES = ['bg-brand-navy', 'bg-brand-purple', 'bg-brand-navy-light', 'bg-primary'] as const

/** Stable per name, so a coworker keeps its colour between renders and pages. */
function toneFor(name: string): (typeof TONES)[number] {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return TONES[hash % TONES.length]
}

export interface CoworkerAvatarProps {
  /** The coworker's display name, used for the monogram and the default alt text. */
  name: string
  /** File name in public/coworkers without the extension, e.g. "ava". */
  photo?: string | null
  /** Pixel size of the square: 20 in the header pill, 32 in chat, 48 on a card. */
  size?: number
  /** A soft ring for the coworker that is working right now. */
  active?: boolean
  /**
   * Alt text. Defaults to the name; pass "" when the name is printed next to the
   * face, so screen readers do not hear it twice (Rule 14).
   */
  alt?: string
  className?: string
}

export function CoworkerAvatar({ name, photo, size = 40, active = false, alt, className }: CoworkerAvatarProps) {
  const [failed, setFailed] = useState(false)
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  const label = alt ?? name
  const monogram = (name || '?').trim().charAt(0).toUpperCase()
  const showPhoto = Boolean(photo) && !failed

  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
      title={label || undefined}
    >
      {active && (
        <span
          aria-hidden='true'
          className='absolute -inset-0.5 rounded-full ring-2 ring-brand-cornflower/60'
        />
      )}
      {showPhoto ? (
        // A small square from /public: next/image would add a loader and buy nothing.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`${basePath}/coworkers/${photo}.jpg`}
          alt={label}
          width={size}
          height={size}
          onError={() => setFailed(true)}
          className='relative rounded-full object-cover ring-1 ring-hairline'
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          role={label ? 'img' : undefined}
          aria-label={label || undefined}
          aria-hidden={label ? undefined : true}
          className={cn(
            'relative flex items-center justify-center rounded-full font-display font-semibold text-white ring-1 ring-hairline',
            toneFor(name)
          )}
          style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }}
        >
          {monogram}
        </span>
      )}
    </span>
  )
}
